'use client'

import { useTRPC } from '@packages/utilities/hooks'
import { Time } from '@packages/utilities/utils'
import { useQuery } from '@tanstack/react-query'
import { Modal } from 'react-bootstrap'
import { useEffect, useState } from 'react'
import { Button } from '../button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh } from '@awesome.me/kit-724bf79b2f/icons/classic/solid'
import { faPartyHorn } from '@awesome.me/kit-724bf79b2f/icons/duotone/solid'

interface Properties {
  initialBuildId: string
}

export const UpdateReminderModal: React.FC<Properties> = ({
  initialBuildId,
}) => {
  const api = useTRPC()
  const [shouldShowModal, setShouldShowModal] = useState(false)

  const buildIdQueryOptions = api.shared.getBuildId.queryOptions(undefined, {
    refetchInterval: new Time({ minutes: 1 }).totalMilliseconds,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  })

  const { data: buildId } = useQuery(buildIdQueryOptions)

  useEffect(() => {
    if (typeof window === 'undefined' || !buildId) return

    const isNewerBuild = buildId !== initialBuildId
    const lastShownBuildId = sessionStorage.getItem('lastShownBuildId')
    const shouldShow = isNewerBuild && lastShownBuildId !== buildId

    if (shouldShow) {
      sessionStorage.setItem('lastShownBuildId', buildId)
      setShouldShowModal(true)
    }
  }, [buildId, initialBuildId])

  const handleClose = () => {
    setShouldShowModal(false)
  }

  return (
    <Modal show={shouldShowModal} onHide={handleClose} centered>
      <Modal.Header className="border-0 pb-0" closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <span>New version available!</span>
          <FontAwesomeIcon
            icon={faPartyHorn}
            className="primary mb-1"
            size="sm"
          />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2 d-flex flex-column gap-2">
        <p>
          A newer version of the app is ready. Refresh to get the latest
          updates.
        </p>
        <p className="text-muted">
          <b>Note:</b> Any unsaved changes will be lost.
        </p>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between border-0 pt-0">
        <Button variant="ghost" onClick={handleClose}>
          Dismiss
        </Button>

        <Button
          variant="primary"
          onClick={() => {
            window.location.reload()
          }}
          icon={<FontAwesomeIcon icon={faRefresh} />}
        >
          Refresh
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
